import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CustomCursor from '@/components/CustomCursor';
import { Toaster } from 'sonner';
import { 
  Shield, Lock, Eye, Server, Cpu, Database, CheckCircle, 
  AlertTriangle, Zap, RefreshCw, Users, FileText, Brain,
  Clock, Activity, Layers, DollarSign, Briefcase, GitBranch,
  Receipt, Calendar
} from 'lucide-react';

const TechSecurityPage = () => {
  const architectureFeatures = [
    {
      icon: Layers,
      title: 'Application & Workflow Layer',
      tagline: 'Orchestrates deals, actions, drafting tasks, approvals, and risk modules.',
      items: [
        'Containerized services (e.g., Kubernetes) with horizontal scaling.',
        'Workflow engine drives Intake, Fast Scan, Deep Dive, and award-package preparation.',
        'All workflow events captured for traceability and oversight.'
      ]
    },
    {
      icon: Database,
      title: 'Data, Evidence & Analytics Layer',
      tagline: 'Operational store plus governed analytics for reporting and AI features.',
      items: [
        'Operational DB for actions, approvals, module outputs, and package artifacts.',
        'Warehouse for historical trends, cycle-time models, and funding-lapse risk.',
        'Semantic layer defines governed metrics and consistent reporting.'
      ]
    },
    {
      icon: Cpu,
      title: 'AI + Portal Boundary Services',
      tagline: 'Governed drafting and controlled submission paths.',
      items: [
        'AI drafting layer for RFI / RFQ / SOW / briefings with approval workflows.',
        'RAG over your approved document sets (templates, policies, prior actions).',
        'Acquisition Portal supports intake/submission events with audit logging.'
      ]
    }
  ];

  const securityPillars = [
    {
      icon: Users,
      title: 'Access Control & Tenancy',
      tagline: 'Aligns with org structure, separation-of-duties, and portal permissions.',
      items: [
        'Role- and attribute-based access control (RBAC/ABAC).',
        'Row-level security so users only see authorized orgs, programs, and actions.',
        'Support for agency identity providers, SSO, and scoped portal roles.'
      ],
      gradient: true
    },
    {
      icon: Lock,
      title: 'Data Protection & Privacy',
      tagline: 'Protects sensitive content across drafting, diligence, and submissions.',
      items: [
        'Encryption in transit (TLS 1.2+) and at rest using strong, FIPS-aligned ciphers.',
        'Logical tenant isolation and strict separation of dev / test / prod.',
        'Optional masking/redaction in analytics and portal-facing views.'
      ],
      gradient: true
    },
    {
      icon: Eye,
      title: 'Audit, Logging & Oversight',
      tagline: 'Workflow, drafting, and submission actions are all reviewable.',
      items: [
        'Audit logs for workflow transitions, approvals, and overrides.',
        'Traceability from findings → drafting outputs → final package decisions.',
        'Planned integration with SIEM tooling for centralized monitoring.'
      ],
      gradient: true
    }
  ];

  const aiGovernance = [
    {
      icon: Users,
      title: 'Human in the Loop, by Design',
      tagline: 'AI assists; humans approve and submit.',
      items: [
        'AI drafts RFI/RFQ/SOW text and summaries. Never auto-approves or submits.',
        'Users accept, edit, or reject suggestions; approvals are explicit workflow events.',
        'Separation between "advice," "draft," and "authorization" is enforced.'
      ]
    },
    {
      icon: FileText,
      title: 'Explainable & Auditable Outputs',
      tagline: 'Every draft can show its evidence.',
      items: [
        '"Why is this included?" links back to inputs: docs, clauses, module findings, and structured fields.',
        'Metadata records model/version, prompt policy, time, and input set.',
        'Drafts are versioned and re-runnable when inputs change.'
      ]
    },
    {
      icon: Shield,
      title: 'Governed Data and Model Choices',
      tagline: 'Only approved models + approved context.',
      items: [
        'AI gateway governed by Adaptive Compound Intelligence (ACI) routes requests to approved models and enforces policy guardrails.',
        'Configurable for government-hosted models where required.',
        'Model registry tracks performance, drift, and review cycles over time.'
      ]
    }
  ];

  const acumenFeatures = [
    {
      title: 'ACUMEN-10 Primary',
      status: 'Active',
      statusColor: 'green',
      desc: 'Main AI engine governed by ACI, optimized for federal acquisition workflows',
      latency: '~1.2s',
      uptime: '99.97%'
    },
    {
      title: 'ACUMEN-10B Hot5 Standby',
      status: 'Standby',
      statusColor: 'blue',
      desc: 'Identical backup ready for instant failover',
      latency: 'Ready',
      uptime: '99.95%'
    },
    {
      title: 'ACUMEN-CORE',
      status: 'Active',
      statusColor: 'green',
      desc: 'Rule-based fallback for offline/degraded scenarios',
      latency: '~0.08s',
      uptime: '100%'
    },
    {
      title: 'ACUMEN-ASSIST',
      status: 'Available',
      statusColor: 'purple',
      desc: 'Human specialist escalation queue',
      latency: 'N/A',
      uptime: '24/7'
    }
  ];

  const selfHealingSteps = [
    { tier: 'Tier 1', title: 'Auto-Correct', desc: 'Minor pattern-based fixes applied automatically in real-time', color: 'green' },
    { tier: 'Tier 2', title: 'Review Queue', desc: 'Flagged outputs and user corrections reviewed weekly', color: 'amber' },
    { tier: 'Tier 3', title: 'Enhancement Backlog', desc: 'Feature requests and optimization opportunities in monthly sprints', color: 'blue' }
  ];

  const dataGovernance = {
    noAccess: [
      'Vendor proprietary pricing',
      'VA budget allocations',
      'PII (names, SSNs, contact info)',
      'Source selection decisions'
    ],
    hasAccess: [
      'AI interaction logs (anonymized)',
      'Error patterns & corrections',
      'Feature usage metrics',
      'User feedback (thumbs up/down)'
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50">
      <CustomCursor />
      <Toaster position="top-center" />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-20 pb-16 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-100/50 via-transparent to-purple-100/50"></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block px-4 py-1.5 bg-indigo-100 text-indigo-700 text-sm font-semibold rounded-full mb-6">
                  Secure by Design • Built for Oversight
                </span>
                <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  Technology and Security for
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                    Award-Ready Federal Work
                  </span>
                </h1>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  AcuSightPro is designed as an acquisition system of record: modern cloud architecture, strong security 
                  controls, and AI that supports FAR/DFARS-aware drafting (RFI, RFQ, SOW, memos) while staying explainable, 
                  auditable, and firmly under human approval.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <a
                    href="/contact"
                    className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-purple-500/30 transition-all hover:-translate-y-0.5 text-center"
                  >
                    Discuss Architecture & Security
                  </a>
                  <a
                    href="/product"
                    className="px-8 py-3 bg-white border-2 border-gray-200 text-gray-700 font-semibold rounded-full hover:border-purple-300 hover:text-purple-700 transition-all text-center"
                  >
                    Back to Product Overview
                  </a>
                </div>
                <p className="text-sm text-gray-500">
                  Built to align with federal expectations and integrate with your identity, document, and acquisition 
                  tooling, without forcing a rip-and-replace.
                </p>
              </div>

              {/* Security Pillars Card */}
              <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-gray-900">Security Pillars for AI + Portal Workflows</h3>
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                    Overview
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Four pillars guide design, implementation, and operations, especially for drafting and submission workflows.
                </p>
                <ul className="space-y-3">
                  {[
                    { label: 'Architecture', desc: 'API-first, containerized services with controlled integration boundaries.' },
                    { label: 'Access Control', desc: 'RBAC/ABAC for workspace + Acquisition Portal permissions.' },
                    { label: 'Data Protection', desc: 'Encryption, tenant isolation, and environment separation.' },
                    { label: 'AI Governance', desc: 'Human approvals, evidence links, and full audit trails for generated content.' }
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold text-gray-900">{item.label}:</span>{' '}
                        <span className="text-gray-600 text-sm">{item.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ACUMEN-10 AI Reliability Section */}
        <section className="py-16 bg-gradient-to-r from-indigo-900 via-purple-900 to-blue-900 text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3 mb-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-2xl font-bold">
                  A7
                </div>
                <div className="text-left">
                  <h2 className="text-3xl font-bold">ACUMEN-10</h2>
                  <p className="text-indigo-300">Encore&apos;s Custom AI Self-Healing Model</p>
                </div>
              </div>
              <p className="text-lg text-indigo-200 max-w-3xl mx-auto">
                A purpose-built AI engine optimized for government acquisition workflows. ACUMEN-10 features automatic error 
                correction, continuous learning from user feedback, and multi-tier failover architecture for 99.97% guaranteed uptime.
              </p>
            </div>

            {/* AI Reliability Stats */}
            <div className="grid md:grid-cols-4 gap-6 mb-12">
              {acumenFeatures.map((feature, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-white">{feature.title}</h4>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      feature.statusColor === 'green' ? 'bg-green-500' :
                      feature.statusColor === 'blue' ? 'bg-blue-500' : 'bg-purple-500'
                    } text-white`}>
                      {feature.status}
                    </span>
                  </div>
                  <p className="text-sm text-indigo-200 mb-3">{feature.desc}</p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-indigo-300">Latency: {feature.latency}</span>
                    <span className="text-indigo-300">Uptime: {feature.uptime}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Failover Architecture */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="text-xl font-bold mb-6 text-center">AI Reliability & Contingency Plan</h3>
              <p className="text-center text-indigo-200 mb-8">
                What happens when ACUMEN-10 experiences issues? AcuSightPro is built with enterprise-grade redundancy 
                to ensure 99.97% uptime and zero data loss.
              </p>
              <div className="grid md:grid-cols-4 gap-4">
                {[
                  { step: '1', title: 'Issue Detected', desc: 'Automatic health monitoring detects latency spike or errors', icon: AlertTriangle },
                  { step: '2', title: 'Instant Failover', desc: 'Traffic routes to ACUMEN-10B hot standby in <2 seconds', icon: RefreshCw },
                  { step: '3', title: 'Graceful Degradation', desc: 'ACUMEN-CORE provides rule-based fallback if needed', icon: Shield },
                  { step: '4', title: 'Human Escalation', desc: 'ACUMEN-ASSIST routes to specialists for complex cases', icon: Users }
                ].map((item, index) => (
                  <div key={index} className="text-center">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mx-auto mb-3">
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-xs text-indigo-400 mb-1">Step {item.step}</div>
                    <h4 className="font-bold text-white mb-1">{item.title}</h4>
                    <p className="text-xs text-indigo-300">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Three-Tier Self-Healing System */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Three-Tier{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                  Continuous Improvement System
                </span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                AcuSightPro uses a structured feedback loop to continuously improve AI accuracy and capabilities.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {selfHealingSteps.map((step, index) => (
                <div
                  key={index}
                  className={`rounded-2xl p-6 border-2 ${
                    step.color === 'green' ? 'border-green-500 bg-green-50' :
                    step.color === 'amber' ? 'border-amber-500 bg-amber-50' :
                    'border-blue-500 bg-blue-50'
                  }`}
                >
                  <div className={`text-xs font-bold mb-2 ${
                    step.color === 'green' ? 'text-green-600' :
                    step.color === 'amber' ? 'text-amber-600' : 'text-blue-600'
                  }`}>
                    {step.tier}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Architecture Section */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-indigo-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Modern Architecture That{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                  Fits Into Your Environment
                </span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                ASP is designed to plug into existing systems instead of replacing them. Data flows are transparent and 
                governed, with clear boundaries between operational records, analytics, the AI drafting layer, and the Acquisition Portal.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {architectureFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:border-purple-200 transition-all"
                  data-robot-tooltip={feature.tagline}
                >
                  <feature.icon className="w-10 h-10 text-purple-600 mb-4" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-500 mb-4">{feature.tagline}</p>
                  <ul className="space-y-2">
                    {feature.items.map((item, i) => (
                      <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 mt-8 justify-center">
              {['API-first', 'Event-capable', 'Separation of duties', 'Traceable data flow'].map((pill, i) => (
                <span key={i} className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm text-gray-600">
                  {pill}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Security Fundamentals */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                  Security and Compliance
                </span>{' '}
                Fundamentals
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Core controls (access, protection, logging) are built into ASP&apos;s design, with room to map into agency-specific 
                policy, control catalogs, and review processes.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {securityPillars.map((pillar, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl p-[2px]"
                >
                  <div className="bg-white rounded-2xl p-6 h-full">
                    <pillar.icon className="w-10 h-10 text-purple-600 mb-4" />
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{pillar.title}</h3>
                    <p className="text-sm text-gray-500 mb-4">{pillar.tagline}</p>
                    <ul className="space-y-2">
                      {pillar.items.map((item, i) => (
                        <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* AI Safety & Governance */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-indigo-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                AI That&apos;s Helpful,{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                  Not Mysterious
                </span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                ASP uses AI to summarize, flag, compare, and draft acquisition artifacts, not to replace judgment. Outputs are 
                advisory, labeled, and tied back to the documents and data they came from.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {aiGovernance.map((gov, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:border-purple-200 transition-all"
                >
                  <gov.icon className="w-10 h-10 text-purple-600 mb-4" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{gov.title}</h3>
                  <p className="text-sm text-gray-500 mb-4">{gov.tagline}</p>
                  <ul className="space-y-2">
                    {gov.items.map((item, i) => (
                      <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 mt-8 justify-center">
              {['AI as co-pilot, not decider', 'Evidence-linked drafting', 'Policy + template allowlists'].map((pill, i) => (
                <span key={i} className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm text-gray-600">
                  {pill}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Data Governance Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Data Governance &{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                  Access Boundaries
                </span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Your sensitive data stays protected. Here&apos;s what Encore can and cannot access.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-red-700">Encore Does NOT Access</h3>
                </div>
                <ul className="space-y-2">
                  {dataGovernance.noAccess.map((item, i) => (
                    <li key={i} className="text-sm text-gray-700 flex items-center gap-2">
                      <span className="text-red-500">✕</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-green-50 border-2 border-green-300 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-green-700">Encore DOES Access</h3>
                </div>
                <ul className="space-y-2">
                  {dataGovernance.hasAccess.map((item, i) => (
                    <li key={i} className="text-sm text-gray-700 flex items-center gap-2">
                      <span className="text-green-500">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Financial Integration */}
        <section className="py-16 bg-gradient-to-br from-emerald-50 via-white to-indigo-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-emerald-100 text-emerald-700 text-sm font-semibold rounded-full mb-4">
                Financial Integration
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                How the Money Plumbing Stays Clean
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                AcuSightPro never replaces your system of record. We hand your established financial platform a clean, validated package every time, with the financial metadata mapped to the right place. Obligations stay accurate. Fund cites stay correct. Audit trails stay continuous.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-4">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Fund Citation Mapping</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  AcuSightPro&rsquo;s fund cite, appropriation, and fiscal year fields map directly to the financial allocation schema of the system of record. Validated against the agency-owned interface specification before push. No re-keying, no drift.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-4">
                  <Briefcase className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Independent Government Cost Estimate</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  IGCE totals, line items, and labor mix flow as a structured payload into the system of record\u2019s acquisition-financial schema. GSA Schedule labor rate benchmarks are pre-checked. Variance band validated against historical awards.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center mb-4">
                  <GitBranch className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Obligation Tracking</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Once the system of record posts the obligation, the inbound feedback loop updates AcuSightPro with current obligated, expended, and remaining balances. Funding lapse alerts fire before expiration.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center mb-4">
                  <Receipt className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Modifications &amp; Adjustments</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Contract modifications recorded in the system of record flow back as modification events. ACI flags anomalies (modifications above forecast, repeated scope changes) for cross-agency benchmark review.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center mb-4">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Closeout &amp; Reconciliation</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Closeout events from the system of record confirm the acquisition is complete. AcuSightPro ties the closeout back to the original AcuSightPro package, source documents, and the team that produced it. Clean record. Clean exit.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Audit Trail &amp; Reconciliation</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Every push, validation, modification, and closeout is timestamped, user-attributed, and payload-hashed. The audit trail joins on the system-of-record transaction ID so financial reconciliation against Treasury disbursements stays continuous.
                </p>
              </div>
            </div>

            <div className="mt-10 bg-white border-2 border-emerald-200 rounded-2xl p-6 max-w-3xl mx-auto text-center">
              <p className="text-sm text-gray-700 leading-relaxed">
                <strong className="text-gray-900">The takeaway:</strong> CFOs, budget officers, and program finance teams should not need to learn a new system. AcuSightPro feeds the system you already trust with cleaner data, better documentation, and a verifiable audit trail. Money flows through your established financial platform the way it always has.
              </p>
              <a href="/financial-integration" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-emerald-900 transition-colors">
                See the full financial integration architecture <span>&rarr;</span>
              </a>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-purple-600 to-blue-600">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to See AcuSightPro in Action?
            </h2>
            <p className="text-lg text-purple-100 mb-8">
              We&apos;re building an acquisition copilot, diligence engine, and command center for modern agencies. 
              Let&apos;s walk you through what investors are backing.
            </p>
            <a
              href="/contact"
              className="inline-flex px-8 py-4 bg-white text-purple-700 font-semibold rounded-full hover:shadow-lg transition-all hover:-translate-y-0.5"
            >
              Contact Us
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TechSecurityPage;
